import { filter } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { Reminder } from '../../../models/reminder';

import { NavbarComponent } from '../../navbar/navbar/navbar.component';

import { AuthService } from '../../../services/common/auth.service';
import { DataStoreService } from '../../../services/common/data-store.service';
import { HttpClientService } from 'src/app/services/common/http-client.service';
import { ModalService } from '../../../services/common/modal.service';
import { NotyService } from '../../../services/common/noty.service';
import { ReminderService } from '../../../services/events/reminder.service';
import { RruleService } from '../../../services/events/rrule.service';
import { UserService } from '../../../services/users/user.service';

@Component({
  selector: 'app-reminder-common',
  templateUrl: './reminder-common.component.html',
  styleUrls: ['./reminder-common.component.scss'],
})
export class ReminderCommonComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Default date for indicating particular dates on new reminders
   */
  @Input() defaultDate: any;

  /**
   * Default title for indicating particular dates
   */
  @Input() defaultTitle: string;

  /**
   * Notifications only (pending reminders)
   */
  @Input() notificationMode: boolean = false;

  /**
   * Notification position
   */
  @Input() notyPosition: string = 'topRight';

  /**
   * Reminderable fields for reminderable
   */
  @Input() reminderable_fields: Array<any> = [];

  /**
   * Reminderable type from parent component
   */
  @Input() reminderable_type: string;

  /**
   * Reminderable id from parent component
   */
  @Input() reminderable_id: number;

  /**
   * Reminder show dialog ref
   * @param {[type]} 'reminder_show' reminder show dialog ref
   */
  @ViewChild('reminder_show', { static: true }) reminderShowDialog: TemplateRef<any>;

  /**
   * Indicates if the component is destroyed so that any ongoing requests can be stopped on result
   * @type {boolean}
   */
  componentDestroyed: boolean = false;

  /**
   * Until date struct
   * @type {any}
   */
  endDateStruct: any;

  /**
   * Form options for the reminder form
   * @type {any}
   */
  formOptions: any = {
    deliver_options: [],
    reminderable_types: [],
    users: [],
  };

  /**
   * Indicates if form has been submitted atleast once
   * @type {boolean}
   */
  formSubmitted: boolean = false;

  /**
   * Indicates if form is being processed
   * @type {boolean}
   */
  formSubmitting: boolean = false;

  /**
   * Indicates if form options is being loaded
   * @type {boolean}
   */
  loadingFormOptions: boolean = false;

  /**
   * Indicates if reminders are being loaded
   * @type {boolean}
   */
  loadingReminders: boolean = false;

  /**
   * Modal object
   * @type {BsModalRef | undefined}
   */
  modalRef: BsModalRef | undefined;

  /**
   * Route events subscription to get change in query params for current page
   * @type {Subscription}
   */
  queryParamsSubscription: Subscription;

  /**
   * Selected recipients
   * @type {Array<any>}
   */
  recipients: Array<any> = [];

  /**
   * Recurring rules object for rrule library (handled by rrule service)
   * @type {any}
   */
  recurring: any = {
    freq: 'NONE',
    interval: 1,
  };

  /**
   * Object to handle recurring days selection, translated to recurring object fields
   * @type {any}
   */
  recurringDays: any = {
    by_day: {},
    by_month_day: {},
    by_week_day: {},
  };

  /**
   * Reminder to show
   * @type {Reminder}
   */
  reminderBeingViewed: Reminder;

  /**
   * Reminders list for the specified reminderable
   * @type {Array<Reminder>}
   */
  reminders: Array<Reminder> = [];

  /**
   * Search params
   * @type {any}
   */
  searchParams: any = {
    type: 'pending',
  };

  /**
   * Temporary variable to hold a selected recipient for adding it
   * @type {any}
   */
  selectedRecipient: any = {};

  /**
   * New reminder or existing reminder being edited
   */
  selectedReminder: Reminder = new Reminder();

  /**
   * Set of selected reminder IDs for batch operations
   * @type {Set<number>}
   */
  selectedReminderIds: Set<number> = new Set();

  /**
   * Indicates if batch dismiss is in progress
   * @type {boolean}
   */
  dismissingSelected: boolean = false;

  /**
   * Indicates if reminder ids are to be shown to the user
   * @type {boolean}
   */
  showId: boolean = false;

  /**
   * Indicates if reminder show dialog is open
   * @type {boolean}
   */
  showingReminder: boolean = false;

  /**
   * Reminder date
   * @type {any}
   */
  startDateStruct: any;

  /**
   * Unique string to save component instance in data store
   * @type {string}
   */
  uniqueDataStoreId: string;

  /**
   * Modal object
   * @type {BsModalRef}
   */
  viewModalRef: BsModalRef;
  canViewAllReminders: boolean;
  users: Array<any> = [];
  userIdForReminders: string;
  /**
   * Constructor function
   * @param {BsModalService}  private modalService    Service for creating modals (from library)
   * @param {NotyService}     private notyService     Service to create notifications
   * @param {ReminderService} private reminderService Reminder resource service
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private dataStoreService: DataStoreService,
    private httpClientService: HttpClientService,
    private modalService: BsModalService,
    private customModalService: ModalService,
    private notyService: NotyService,
    private reminderService: ReminderService,
    private router: Router,
    public rruleService: RruleService,
    private userService: UserService
  ) {
    this.rruleService.setVariables();
    this.uniqueDataStoreId = this.dataStoreService.randomString();
  }

  // Component lifecycle methods

  /**
   * Called when component finishes initialization
   */
  ngOnInit() {
    this.authService.getCurrentUserGroups().then((res) => {
      if (this.componentDestroyed) return;
      this.userIdForReminders = res.current_user_id.toString();
      this.loadReminders();
      for (let group of res.groups) {
        if (group === 'tech' || 'admin') {
          this.canViewAllReminders = true;
        }
      }
      if (this.canViewAllReminders) {
        this.userService.index().then((re) => {
          if (this.componentDestroyed) return;
          re.users.unshift({ id: this.userIdForReminders, full_name: 'No Selection' });
          this.users = re.users;
        });
      }
    });
    this.dataStoreService.save(this.uniqueDataStoreId, this, 'reminderStore');
    if (this.notificationMode)
      this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe((queryParams) => {
        if (queryParams.reminder_id && this.selectedReminder && String(this.selectedReminder.id) != String(queryParams.reminder_id)) {
          this.loadReminder(queryParams.reminder_id);
        }
      });
    this.reminderService
      .formOptions2({
        reminderable_id: this.reminderable_id,
        reminderable_type: this.reminderable_type,
      })
      .then((response_json) => {
        if (this.componentDestroyed) return;
        this.formOptions = response_json;
      });
    this.loadReminders();
    this.authService.techDebug().then((techDebug) => {
      if (this.componentDestroyed) return;
      this.showId = techDebug;
    });
  }

  /**
   * Called when component is destroyed
   */
  ngOnDestroy() {
    this.dataStoreService.remove(this.uniqueDataStoreId, 'reminderStore');
    this.componentDestroyed = true;
    if (this.modalRef) this.modalRef.hide();
    if (this.queryParamsSubscription) this.queryParamsSubscription.unsubscribe();
  }

  /**
   * Called when component instance variables change
   * @param {SimpleChanges} changes [description]
   */
  ngOnChanges(changes: SimpleChanges) {
    if ((changes.reminderable_id && changes.reminderable_id.currentValue) || (changes.reminderable_id && changes.reminderable_id.currentValue)) {
      this.reminderService
        .formOptions2({
          reminderable_id: this.reminderable_id,
          reminderable_type: this.reminderable_type,
        })
        .then((response_json) => {
          if (this.componentDestroyed) return;
          this.formOptions = response_json;
        });
      this.loadReminders();
      this.selectedReminder.reminderable_type = this.reminderable_type;
      this.selectedReminder.reminderable_id = this.reminderable_id;
    }
  }

  // Component methods

  /**
   * Build rrule for recurring events
   */
  buildRrule() {
    let ruleObject: any = this.rruleService.buildRrule(this.recurring, this.recurringDays);
    this.selectedReminder.rrule = ruleObject.string;
    this.selectedReminder.rrule_text = ruleObject.text;
  }

  /**
   * Build rrule objects
   */
  buildRruleObjects() {
    let rules: any = this.rruleService.buildRruleObjects(this.selectedReminder.rrule);
    this.selectedReminder.rrule_text = rules.rrule_text;
    if (rules.recurring) this.recurring = rules.recurring;
    if (rules.recurringDays) this.recurringDays = rules.recurringDays;
  }

  /**
   * Close form modal
   */
  closeFormModal() {
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = undefined;
    }
    if (this.showingReminder && this.selectedReminder.id) this.showReminder(this.selectedReminder, this.reminderShowDialog);
    else {
      this.selectedReminder = new Reminder();
      this.selectedReminder.reminderable_type = this.reminderable_type;
      this.selectedReminder.reminderable_id = this.reminderable_id;
    }
    this.recurring = {
      freq: 'NONE',
      interval: 1,
    };
    this.recurringDays = {
      by_day: {},
      by_month_day: {},
      by_week_day: {},
    };
    this.recipients = [];
  }

  /**
   * Delete reminder
   * @param {any}    reminder Reminder to delete
   * @param {number} index    Position of reminder in list
   */
  deleteReminder(reminder: any, index: number | null = null) {
    this.customModalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.customModalService.confirmDialogComponent.open(`<p>Delete Reminder - <strong class='text-danger'>${reminder.topic}</strong></p>`).result.then(
      (result) => {
        this.customModalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          reminder.deleting = true;
          this.reminderService
            .delete(reminder.id)
            .then((response_json) => {
              reminder.deleting = false;
              if (response_json.success) {
                this.notyService.notify(response_json.text || `Reminder deleted - ${reminder.topic}`, 'warning');
                if (index) this.reminders.splice(index, 1);
                else {
                  this.updateAllReminderComponents();
                  if (this.modalRef) this.modalRef.hide();
                  this.showingReminder = false;
                }
              } else this.notyService.notify(response_json.text || `Error deleting reminder`, 'error');
            })
            .catch(() => {
              reminder.deleting = false;
              this.notyService.notify('Error deleting reminder', 'error');
            });
        }
      },
      (reason) => {
        this.customModalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Dismiss reminder
   * @param {any} reminder Reminder to dismiss
   */
  dismiss(reminder: any) {
    reminder.dismissing = true;
    this.reminderService
      .dismiss(reminder.id)
      .then((response_json) => {
        reminder.dismissing = false;
        let notificationPosition: string = this.notificationMode ? 'topRight' : this.notyPosition || 'topRight';
        if (response_json.success) {
          reminder.opened = true;
          this.notyService.notify(response_json.text || 'Reminder dismissed', 'success', 5000, notificationPosition);
          this.updateAllReminderComponents();
        } else this.notyService.notify(response_json.text || 'Error dismissing reminder', 'error', 5000, notificationPosition);
      })
      .catch(() => {
        reminder.dismissing = false;
        this.notyService.notify('Error dismissing reminder', 'error', 5000, this.notyPosition || 'topRight');
      });
  }

  /**
   * Toggle selection of a reminder for batch operations
   * @param {number} reminderId Reminder ID to toggle
   */
  toggleReminderSelection(reminderId: number) {
    if (this.selectedReminderIds.has(reminderId)) {
      this.selectedReminderIds.delete(reminderId);
    } else {
      this.selectedReminderIds.add(reminderId);
    }
  }

  /**
   * Check if a reminder is selected
   * @param {number} reminderId Reminder ID to check
   * @returns {boolean} True if reminder is selected
   */
  isReminderSelected(reminderId: number): boolean {
    return this.selectedReminderIds.has(reminderId);
  }

  /**
   * Toggle select/deselect all reminders
   */
  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedReminderIds.clear();
    } else {
      this.selectedReminderIds.clear();
      for (let reminder of this.reminders) {
        this.selectedReminderIds.add(reminder.id);
      }
    }
  }

  /**
   * Dismiss all selected reminders
   */
  dismissSelectedReminders() {
    if (this.selectedReminderIds.size === 0) return;

    this.customModalService.confirmDialogComponent.open(
      `<p>Dismiss <strong>${this.selectedReminderIds.size}</strong> selected reminder(s)?</p>`
    ).result.then(
      (result) => {
        if (result == 'confirmed') {
          this.dismissingSelected = true;
          let dismissPromises = Array.from(this.selectedReminderIds).map(id =>
            this.reminderService.dismiss(id)
          );

          Promise.all(dismissPromises).then((responses) => {
            this.dismissingSelected = false;
            let successCount = responses.filter(r => r.success).length;
            this.selectedReminderIds.clear();
            let notificationPosition: string = this.notificationMode ? 'topRight' : this.notyPosition || 'topRight';
            if (successCount > 0) {
              this.notyService.notify(`${successCount} reminder(s) dismissed`, 'success', 5000, notificationPosition);
            }
            if (successCount < responses.length) {
              this.notyService.notify(`${responses.length - successCount} reminder(s) failed to dismiss`, 'error', 5000, notificationPosition);
            }
            this.updateAllReminderComponents();
          }).catch(() => {
            this.dismissingSelected = false;
            this.notyService.notify('Error dismissing some reminders', 'error', 5000, this.notyPosition || 'topRight');
            this.updateAllReminderComponents();
          });
        }
      }
    );
  }

  /**
   * Get whether any reminders are selected
   * @returns {boolean} True if any reminders are selected
   */
  get hasSelectedReminders(): boolean {
    return this.selectedReminderIds.size > 0;
  }

  /**
   * Get whether all reminders are selected
   * @returns {boolean} True if all reminders are selected
   */
  get allSelected(): boolean {
    return this.reminders.length > 0 && this.selectedReminderIds.size === this.reminders.length;
  }

  /**
   * Edit reminder
   * @param {any} reminder Reminder to edit
   */
  editReminder(reminder: any, template: TemplateRef<any>, fromReminderShow: boolean = false, config: any = { backdrop: true, class: 'modal-lg', ignoreBackdropClick: false }) {
    if (!fromReminderShow) this.showingReminder = false;
    else this.showingReminder = true;
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = undefined;
    }
    this.reminderService.edit(reminder.id).then((response_json) => {
      if (!this.reminderable_type || !this.reminderable_id) {
        this.formOptions.title = undefined;
        this.reminderService
          .formOptions2({
            reminderable_id: response_json.reminderable_id,
            reminderable_type: response_json.reminderable_type,
          })
          .then((response_json) => {
            if (response_json.title) this.formOptions.title = response_json.title;
          });
      }
      this.selectedReminder = response_json;
      this.startDateStruct = response_json.start_date_struct;
      this.endDateStruct = response_json.end_date_struct;
      if (this.selectedReminder.delivery_methods) for (let deliveryOption of this.formOptions.deliver_options) if (this.selectedReminder.delivery_methods.indexOf(deliveryOption.value) > -1) deliveryOption.checked = true;
      if (this.selectedReminder.recipient_ids) for (let recipient of this.recipients) if (this.selectedReminder.recipient_ids.indexOf(recipient.value) > -1) this.recipients.push(recipient);
      if (this.selectedReminder.rrule) {
        let rruleObjects: any = this.rruleService.buildRruleObjects(this.selectedReminder.rrule);
        this.selectedReminder.rrule_text = rruleObjects.rrule_text;
        this.recurring = rruleObjects.recurring;
        this.recurringDays = rruleObjects.recurringDays;
      }
      if (this.selectedReminder.rrule && !this.selectedReminder.end_date) this.selectedReminder.repeats_forever = true;
      this.modalRef = this.modalService.show(template, config);
    });
  }

  loadReminder(reminder_id: number, hideReminderableRow: boolean = false) {
    this.reminderService
      .get(reminder_id)
      .then((response_json) => {
        if (!response_json.reminder) this.notyService.notify('Error getting reminder', 'error');
        else {
          let reminder: any = response_json.reminder;
          reminder.hideReminderableRow = hideReminderableRow;
          this.showReminder(response_json.reminder, this.reminderShowDialog);
        }
      })
      .catch(() => {
        this.notyService.notify('Error getting reminder', 'error');
      });
  }

  /**
   * Load reminders
   */
  loadReminders(type: string | null = null, user_id: string = this.userIdForReminders) {
    if (type) this.searchParams.type = type;
    this.searchParams.angular2 = true;
    this.searchParams.reminderable_id = this.reminderable_id;
    this.searchParams.reminderable_type = this.reminderable_type;
    this.searchParams.user_id = user_id;
    if (user_id) {
      this.loadingReminders = true;
      this.reminderService
        .index2(this.searchParams)
        .then((response_json) => {
          if (this.componentDestroyed) return;
          this.loadingReminders = false;
          this.reminders = response_json.reminders;
          if (this.reminders)
            for (let reminder of this.reminders) {
              if (reminder.topic && reminder.topic.length > 30) reminder.topic_str = `${reminder.topic.slice(0, 29)}...`;
              else if (reminder.topic) reminder.topic_str = reminder.topic;              
              if (reminder.reminderable_title){                
                if(reminder.reminderable_title.length > 30) reminder.reminderable_title_str = `${reminder.reminderable_title.slice(0, 29)}...`;
                else reminder.reminderable_title_str = `${reminder.reminderable_title}`;
              }else if (reminder.topic) reminder.reminderable_title_str = reminder.reminderable_title;
              if (reminder.body && reminder.body.length > 30) reminder.body_str = `${reminder.body.slice(0, 29)}...`;
              else if (reminder.body) reminder.body_str = reminder.body;
              if (reminder.rrule) {
                if (reminder.next_date_str) reminder.recurring_tooltip = 'Recurring every: ' + reminder.rrule_text + (reminder.yearly_date ? ' ' + reminder.yearly_date : reminder.end_date_str ? `till ${reminder.end_date_str}` : '');
                else reminder.recurring_tooltip = 'Reminder has ended';
              }
              reminder.tooltip = [reminder.reminderable_title, reminder.topic, reminder.body].filter(Boolean).join('\n');
            }
          if (this.notificationMode) {
            let navbarComponent: NavbarComponent = this.dataStoreService.retrieve('navbarComponent');
            if (navbarComponent) {
              if (this.reminders && this.reminders.length > 0) navbarComponent.pendingNotificationsPresent = true;
              else navbarComponent.pendingNotificationsPresent = false;
            }
          }
        })
        .catch((err) => {
          if (this.componentDestroyed) return;
          console.log('load reminders error', err);
          this.loadingReminders = false;
          this.reminders = [];
        });
    } else {
      this.reminders = [];
    }
  }

  /**
   * Open Modal for form
   * @param {TemplateRef<any>} template Form template from the view
   * @param {false         }}     config        config for the modal with defaults
   */
  openFormModal(template: TemplateRef<any>, field: string = '', config: any = { backdrop: true, class: 'modal-lg', ignoreBackdropClick: false }) {
    this.showingReminder = false;
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = undefined;
    }
    this.selectedReminder = new Reminder();
    this.selectedReminder.reminderable_type = this.reminderable_type;
    this.selectedReminder.reminderable_id = this.reminderable_id;
    this.recurring = {
      freq: 'NONE',
      interval: 1,
    };
    this.recurringDays = {
      by_day: {},
      by_month_day: {},
      by_week_day: {},
    };
    this.recipients = [];
    if (field)
      this.reminderService
        .formOptions2({
          for_field: field,
          reminderable_id: this.reminderable_id,
          reminderable_type: this.reminderable_type,
        })
        .then((response_json) => {
          this.formOptions = response_json;
          if (this.formOptions.description) this.selectedReminder.topic = this.formOptions.description;
          if (this.formOptions.default_date) {
            this.selectedReminder.start_date_struct = this.formOptions.default_date;
            this.startDateStruct = this.formOptions.default_date;
          }
          this.selectedReminder.reminderable_type = this.reminderable_type;
          this.selectedReminder.reminderable_id = this.reminderable_id;
          this.modalRef = this.modalService.show(template, config);
        });
    else {
      this.startDateStruct = undefined;
      this.modalRef = this.modalService.show(template, config);
    }
    if (!this.reminderable_type || !this.reminderable_id) this.formOptions.title = undefined;
  }

  get pdfUrl(): string {
    return this.httpClientService.buildNewPageUrl('/reminders.pdf', this.searchParams);
  }

  /**
   * Remove a selected recipient
   * @param {any} recipient Recipient to remove
   */
  removeRecipient(recipient: any) {
    let position: string | undefined = undefined;
    for (let index in this.recipients)
      if (this.recipients[index].value == recipient.value) {
        position = index;
        break;
      }
    if (position || position === '0') this.recipients.splice(Number(position), 1);
  }

  /**
   * Save Reminder
   */
  save() {
    this.formSubmitted = true;
    let recipient_ids: Array<number> = [];
    let delivery_methods: Array<any> = [];
    for (let recipient of this.recipients) recipient_ids.push(recipient.value);
    for (let deliveryOption of this.formOptions.deliver_options) if (deliveryOption.checked) delivery_methods.push(deliveryOption.value);
    let errors: Array<string> = [];
    if (!this.selectedReminder.topic && (this.selectedReminder.reminderable_type == 'Contact' || this.selectedReminder.reminderable_type == 'Matter')) errors.push('For description is required.');
    if (!this.selectedReminder.start_date_struct) errors.push('Start date is required.');
    if (recipient_ids.length < 1 && !this.selectedReminder.id) errors.push('Atleast one recipient is required.');
    if (delivery_methods.length < 1) errors.push('Atleast one notification option has to be checked.');
    if (!this.selectedReminder.reminderable_id || !this.selectedReminder.reminderable_type) errors.push('Reminder For must be selected.');
    if (errors.length > 0) {
      this.notyService.notify(errors.join(' '), 'error', 10000);
      return;
    }
    this.selectedReminder.delivery_methods = delivery_methods;
    this.selectedReminder.recipient_ids = recipient_ids;
    if (this.reminderable_type) this.selectedReminder.reminderable_type = this.reminderable_type;
    if (this.reminderable_id) this.selectedReminder.reminderable_id = this.reminderable_id;
    this.formSubmitting = true;
    this.reminderService
      .save(this.selectedReminder, true)
      .then((response_json) => {
        this.formSubmitting = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Reminder saved', 'success', '2000');
          this.updateAllReminderComponents();
          for (let deliveryOption of this.formOptions.deliver_options) deliveryOption.checked = false;
          this.closeFormModal();
        } else this.notyService.notify(response_json.text || 'Error saving reminder', 'error', 3000);
      })
      .catch(() => {
        this.formSubmitting = false;
        this.notyService.notify('Error saving reminder', 'error', 3000);
      });
  }

  /**
   * Add selected recipient to list
   */
  selectRecipient() {
    if (this.selectedRecipient && this.selectedRecipient.value) {
      for (let recipient of this.recipients)
        if (recipient.value == this.selectedRecipient.value) {
          this.selectedRecipient = {};
          return;
        }
      this.recipients.push(this.selectedRecipient);
      if (this.selectedReminder.delivery_methods.indexOf('Phone Text') > -1 && !this.selectedRecipient.allow_phone_text) this.notyService.notify(`${this.selectedRecipient.label} has phone text notification disabled`, 'error', 5000, 'center');
      this.selectedRecipient = {};
    } else this.selectedRecipient = {};
  }

  /**
   * Set delivery methods on change in UI
   */
  setDeliveryMethods() {
    this.selectedReminder.delivery_methods = [];
    for (let deliveryOption of this.formOptions.deliver_options) if (deliveryOption.checked) this.selectedReminder.delivery_methods.push(deliveryOption.value);
  }

  /**
   * Show Reminder dialog
   * @param {any}              reminder Reminder to view
   * @param {TemplateRef<any>} template Form template from the view
   * @param {false         }}  config   config for the modal with defaults
   */
  showReminder(reminder: any, template: TemplateRef<any> | any = null, config: any = { backdrop: true, ignoreBackdropClick: false }) {
    template = template || this.reminderShowDialog;
    this.showingReminder = true;
    this.selectedReminder = reminder;
    this.modalRef = this.modalService.show(template, config);
  }

  /**
   * Snooze reminder
   * @param {any} reminder Reminder to snoozed
   */
  snooze(reminder: any) {
    reminder.snoozing = true;
    this.reminderService
      .snooze(reminder.id)
      .then((response_json) => {
        reminder.snoozing = false;
        if (response_json.success) {
          reminder.reminded_at = undefined;
          reminder.snoozed_till = 'snoozed';
          this.notyService.notify(response_json.text || 'Reminder snoozed', 'success', 5000, this.notyPosition || 'topRight');
          this.updateAllReminderComponents();
        } else this.notyService.notify(response_json.text || 'Error snoozing reminder', 'error', 5000, this.notyPosition || 'topRight');
      })
      .catch(() => {
        reminder.snoozing = false;
        this.notyService.notify('Error snoozing reminder', 'error', 5000, this.notyPosition || 'topRight');
      });
  }

  /**
   * Update all reminder components showing the same data
   */
  updateAllReminderComponents() {
    this.dataStoreService.updateReminderComponents();
  }
}
